import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getLocalizedApiMessage,
  showApiErrorToast,
  showApiSuccessToast,
} from "../../../utils/apiMessageHelper";
import {
  getDsgLookup,
  getDsgSettings,
  updateDsgSettingsRequest,
} from "../../../services/api/dsgSettings/dsgSettingsApi";

export const fetchDsgSettings = createAsyncThunk(
  "dsgSettings/fetchDsgSettings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getDsgSettings();
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch DSG settings");
    }
  },
);

const createFetchThunk = (name, endpoint) =>
  createAsyncThunk(`dsgSettings/${name}`, async (_, { rejectWithValue }) => {
    try {
      const response = await getDsgLookup(endpoint);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        `Failed to fetch ${name.replace(/([A-Z])/g, " $1").toLowerCase()}`,
      );
    }
  });

export const fetchTsiStandardNumber = createFetchThunk(
  "fetchTsiStandardNumber",
  "/get/dsg-standard_number",
);
export const fetchTsiContainerTypes = createFetchThunk(
  "fetchTsiContainerTypes",
  "/get/dsg-container-types",
);
export const fetchTsiSignatureFormat = createFetchThunk(
  "fetchTsiSignatureFormat",
  "/get/dsg-signature-formate",
);
export const fetchTsiSignaturePackaging = createFetchThunk(
  "fetchTsiSignaturePackaging",
  "/get/dsg-signature-packaging",
);
export const fetchTsiSignatureOptionSign = createFetchThunk(
  "fetchTsiSignatureOptionSign",
  "/get/dsg-signature-option_sign",
);
export const fetchTsiSignatureLevels = createFetchThunk(
  "fetchTsiSignatureLevels",
  "/get/dsg-signature-levels",
);
export const fetchTsiSignatureDigestAlgorithm = createFetchThunk(
  "fetchTsiSignatureDigestAlgorithm",
  "/get/dsg-signature-digest-algorithm",
);
export const fetchTsiSignatureTokenApis = createFetchThunk(
  "fetchTsiSignatureTokenApis",
  "/get/dsg-signature-token-apis",
);

const toastMessages = {
  update: {
    en: "DSG settings updated successfully!",
    ar: "تم تحديث إعدادات DSG بنجاح!",
  },
  updateError: {
    en: "Failed to update DSG settings.",
    ar: "فشل في تحديث إعدادات DSG.",
  },
};

export const updateDsgSettings = createAsyncThunk(
  "dsgSettings/updateDsgSettings",
  async (settingsData, { rejectWithValue }) => {
    try {
      const response = await updateDsgSettingsRequest(settingsData);
      if (response.data?.success === false) {
        return rejectWithValue(
          getLocalizedApiMessage(response.data, toastMessages.updateError),
        );
      }
      return {
        data: response.data?.data ?? response.data,
        apiMessage: response.data?.message,
      };
    } catch (error) {
      return rejectWithValue(
        getLocalizedApiMessage(error, toastMessages.updateError),
      );
    }
  },
);

const dsgSettingsSlice = createSlice({
  name: "dsgSettings",
  initialState: {
    settings: null,
    tsiTypes: [],
    tsiStandards: [],
    tsiStandardNumbers: [],
    tsiContainerTypes: [],
    tsiSignatureFormat: [],
    tsiSignaturePackaging: [],
    tsiSignatureOptionSigns: [],
    tsiSignatureLevels: [],
    tsiSignatureDigestAlgorithms: [],
    tsiSignatureTokenApis: [],
    loading: false,
    error: null,
  },
  reducers: {
    updateField: (state, action) => {
      const { name, value } = action.payload;
      if (state.settings && state.settings[name] !== undefined) {
        state.settings[name] = value;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTsiStandardNumber.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTsiStandardNumber.fulfilled, (state, action) => {
        state.loading = false;
        state.tsiStandardNumbers = action.payload || [];
      })
      .addCase(fetchTsiStandardNumber.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchTsiContainerTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTsiContainerTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.tsiContainerTypes = action.payload || [];
      })
      .addCase(fetchTsiContainerTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchTsiSignatureFormat.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTsiSignatureFormat.fulfilled, (state, action) => {
        state.loading = false;
        state.tsiSignatureFormat = action.payload || [];
      })
      .addCase(fetchTsiSignatureFormat.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchTsiSignaturePackaging.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTsiSignaturePackaging.fulfilled, (state, action) => {
        state.loading = false;
        state.tsiSignaturePackaging = action.payload || [];
      })
      .addCase(fetchTsiSignaturePackaging.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchTsiSignatureOptionSign.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTsiSignatureOptionSign.fulfilled, (state, action) => {
        state.loading = false;
        state.tsiSignatureOptionSigns = action.payload || [];
      })
      .addCase(fetchTsiSignatureOptionSign.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchTsiSignatureLevels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTsiSignatureLevels.fulfilled, (state, action) => {
        state.loading = false;
        state.tsiSignatureLevels = action.payload || [];
      })
      .addCase(fetchTsiSignatureLevels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchTsiSignatureDigestAlgorithm.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTsiSignatureDigestAlgorithm.fulfilled, (state, action) => {
        state.loading = false;
        state.tsiSignatureDigestAlgorithms = action.payload || [];
      })
      .addCase(fetchTsiSignatureDigestAlgorithm.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchTsiSignatureTokenApis.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTsiSignatureTokenApis.fulfilled, (state, action) => {
        state.loading = false;
        state.tsiSignatureTokenApis = action.payload || [];
      })
      .addCase(fetchTsiSignatureTokenApis.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateDsgSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDsgSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = action.payload?.data ?? action.payload;
        showApiSuccessToast(
          action.payload?.apiMessage,
          toastMessages.update,
        );
      })
      .addCase(updateDsgSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showApiErrorToast(action.payload, toastMessages.updateError);
      })
      .addCase(fetchDsgSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDsgSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = action.payload;
      })
      .addCase(fetchDsgSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { updateField } = dsgSettingsSlice.actions;
export default dsgSettingsSlice.reducer;
